import {ServiceBase} from '../ServiceBase';
import {IServiceFactoryExtended} from '../service-factory-extended.interface';
import {CGPEventSubscription} from '../sdk/cgp-sdk.interface';
import {OnGameOpenParams} from '../sdk/sdk-events';
import {OpenGameParameters} from '../games/game-info';
import {action, IReactionDisposer, observable, reaction} from 'mobx';
import {Uri} from '../location/uri/uri';


export class GameRouteHandlerService extends ServiceBase {
  constructor(services: IServiceFactoryExtended) {
    super(services);
    this._subscribeToGameEvents();

    this._subscribeToUriAndGamesMapChanges(true);
  }



  private _onGameOpenSubscription: CGPEventSubscription;
  private _onGameCloseSubscription: CGPEventSubscription;
  private _currentUriAndGamesMapReaction: IReactionDisposer | null = null;


  private _subscribeToUriAndGamesMapChanges(isFirstSubscription: boolean) {
    this._currentUriAndGamesMapReaction = reaction(() => {
        return {
          uri: this.services.location.currentUri
        }
      },
      (data) => {
        if (this._isOpenGameUri(data.uri)) {
          this._tryOpenGameFromUrl();
        } else {
          this.services.sdk.closeRunningGame();
        }
      }, {fireImmediately: isFirstSubscription});
  }

  private _unsubscribeFromUriAndGamesMapChanges() {
    if(this._currentUriAndGamesMapReaction) {
      this._currentUriAndGamesMapReaction();
      this._currentUriAndGamesMapReaction = null;
    }
  }


  private _subscribeToGameEvents() {
    this._onGameOpenSubscription = this.services.sdk.events.onGameOpen(this._onGameOpen.bind(this));
    this._onGameCloseSubscription = this.services.sdk.events.onGameClose(this._onGameClose.bind(this));
  }

  private _unsubscribeFromGameEvents() {
    if(this._onGameOpenSubscription) {
      this._onGameOpenSubscription.off();
    }

    if(this._onGameCloseSubscription) {
      this._onGameCloseSubscription.off();
    }
  }

  private _getOpenGameFragment(gameType: number, isReal: boolean) {
    return `#page/game/${gameType}/real/${isReal ? '1' : '0'}`;
  }

  private _isOpenGameUri(uri: Uri): boolean {
    if(uri.hash.value.startsWith('page/game')) {
      return true;
    } else {
      return false;
    }
  }

  private _shouldSkipNextCloseGameRedirect: boolean = false;

  private _previousUri: Uri | null = null;
  private _onGameOpen(eventParams: OnGameOpenParams): void {

    if(!this._isOpenGameUri(this.services.location.currentUri)) {
      this._previousUri = this.services.location.currentUri;
    }

    this._unsubscribeFromUriAndGamesMapChanges();
    this.services.location.push(this._getOpenGameFragment(eventParams.gameType, eventParams.isReal)).finally(() => this._subscribeToUriAndGamesMapChanges(false));
  }

  private _onGameClose(): void {
    if(this._shouldSkipNextCloseGameRedirect){
      this._shouldSkipNextCloseGameRedirect = false;
      return;
    }

    let urlToGoBack = this._previousUri ? this._previousUri.toString() : this.services.location.currentUri.pathName.toString();
    this.services.location.push(urlToGoBack);
  }

  @observable.ref lastAttemptToOpenGameFromUrl: number;
  @action.bound
  private _setLastAttemptToOpenGameFromUrl() {
    this.lastAttemptToOpenGameFromUrl = Date.now();
  }

  private _isOpenGameFromUrlInProgress = false;
  async _tryOpenGameFromUrl() {
    if(this._isOpenGameFromUrlInProgress) {
      return;
    }

    let hash = this.services.location.currentUri.hash;

    if(hash.isEmpty) {
      return;
    }

    let currentFragment = hash.value;

    let fragmentParts = currentFragment.split('/');

    if((fragmentParts[0] || '').toLowerCase() !== 'page'
      || (fragmentParts[1] || '').toLowerCase() !== 'game') {
      return;
    }
    let gameType = fragmentParts[2];
    let gameMode = fragmentParts[4];

    let gameInfo = await this.services.games.getGameInfo(parseInt(gameType));
    if(!gameInfo) {
      this._onGameClose();
      this._setLastAttemptToOpenGameFromUrl();
      return;
    }

    let openGameParams: OpenGameParameters = {
      isRealMode: gameMode === '1'
    };

    this._isOpenGameFromUrlInProgress = true;
    this._unsubscribeFromGameEvents();
    try {
      await gameInfo.open(openGameParams);
    }
    catch (err) {
      this._onGameClose();
      this.services.logger.error(`Failed to open game from URL: ${this.services.location.currentUri.toString()}`, err);
    }
    finally {
      this._isOpenGameFromUrlInProgress = false;
      this._subscribeToGameEvents();
      this._setLastAttemptToOpenGameFromUrl();
    }
  }

  redirectToHistory(): void {
    this._shouldSkipNextCloseGameRedirect = true;
    return this.services.sdk.redirectToHistory();
  }

}
